/* Runtime diagnostics for stylesheet loading.
 * Activate by appending ?debugStyles=1 to the URL.
 */
(function(){
  try {
    if(!/debugStyles=1/.test(window.location.search)) return;
    const log = (...a)=>console.log('[StyleDebug]', ...a);
    log('Starting diagnostics...');

    const KEY_SELECTORS = [
      '.privacy-header',
      '.m-tabbar',
      '.create-event-container',
      '.plan-item',
      '.about-header',
      '.m-landing-screen'
    ];

    function summarizeSheets(){
      const sheets = Array.from(document.styleSheets);
      return sheets.map(s => {
        let rulesCount = 0;
        try { rulesCount = s.cssRules ? s.cssRules.length : 0; } catch(e) { rulesCount = 'blocked(CORS)'; }
        return { href: s.href, disabled: s.disabled, rules: rulesCount };
      });
    }

    function sampleSelectors(){
      const sheets = Array.from(document.styleSheets).filter(s=>s.href && /main\./.test(s.href));
      const result = {};
      for(const sel of KEY_SELECTORS){
        result[sel] = false;
      }
      for(const sheet of sheets){
        try {
          const rules = sheet.cssRules || [];
          for(let i=0;i<rules.length;i++){
            const r = rules[i];
            if(r && r.selectorText){
              KEY_SELECTORS.forEach(sel=>{
                if(!result[sel] && r.selectorText.includes(sel)) result[sel] = true;
              });
            }
          }
        } catch(e){ /* ignore */ }
      }
      return result;
    }

    function logTokens(){
      const root = getComputedStyle(document.documentElement);
      const sample = ['--color-brand-primary','--color-text-primary','--semantic-button-primary-bg'];
      const values = sample.reduce((acc,k)=>{ acc[k]=root.getPropertyValue(k)||'(empty)'; return acc; }, {});
      log('Sample token values:', values);
    }

    function check(label){
      log(label || 'Pass', 'Stylesheet summary:', summarizeSheets());
      logTokens();
      const selSample = sampleSelectors();
      log('Selector sample presence:', selSample);
      const brand = getComputedStyle(document.documentElement).getPropertyValue('--color-brand-primary');
      if(!brand.trim()) {
        log('Brand token empty. Forcing re-eval.');
        document.documentElement.classList.add('style-debug-touch');
        setTimeout(()=>{
          const retry = getComputedStyle(document.documentElement).getPropertyValue('--color-brand-primary');
          log('Post-touch brand token:', retry);
        }, 50);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => check('Initial (DOMContentLoaded)'));
    } else {
      check('Initial');
    }

    // Second pass after async dynamic imports (if any)
    setTimeout(()=>check('Delayed 1000ms pass'), 1000);

    // --- Advanced analysis: fetch raw CSS and attempt to detect parse cutoff ---
    (async function advancedAnalysis(){
      try {
        const sheet = Array.from(document.styleSheets).find(s=>s.href && /main\.[^.]+\.css/.test(s.href));
        if(!sheet || !sheet.href){
          log('AdvancedAnalysis: main.* stylesheet not found for deep inspection.');
          return;
        }
        const href = sheet.href;
        const t0 = performance.now();
        const resp = await fetch(href, { cache: 'no-store' });
        const text = await resp.text();
        const t1 = performance.now();
        log('AdvancedAnalysis: fetched raw CSS', { url: href, bytes: text.length, ms: +(t1-t0).toFixed(1) });

        const KEY_SELECTORS = [
          '.privacy-header',
          '.m-tabbar',
          '.create-event-container',
          '.plan-item',
          '.about-header',
          '.m-landing-screen'
        ];
        const selectorPositions = KEY_SELECTORS.reduce((acc, sel)=>{ acc[sel] = text.indexOf(sel); return acc; }, {});
        log('AdvancedAnalysis: selector text positions (raw file indexes):', selectorPositions);

        // Quick structural sanity checks
        const openBraces = (text.match(/\{/g)||[]).length;
        const closeBraces = (text.match(/\}/g)||[]).length;
        if(openBraces !== closeBraces){
          log('AdvancedAnalysis WARNING: brace imbalance', { openBraces, closeBraces });
        }

        // Detect any NULL or control chars that might upset parser
        const badChars = [];
        for(let i=0;i<text.length;i++){
          const code = text.charCodeAt(i);
          if(code === 0 || (code < 9) || (code >= 14 && code < 32)){
            badChars.push({ idx:i, code });
            if(badChars.length > 10) break;
          }
        }
        if(badChars.length){
          log('AdvancedAnalysis WARNING: control characters present', badChars);
        }

        // Attempt parse via Constructable Stylesheet to count rules independently.
        let constructableRuleCount = null;
        if('CSSStyleSheet' in window && CSSStyleSheet.prototype.replaceSync){
          try {
            const cssSheet = new CSSStyleSheet();
            cssSheet.replaceSync(text);
            constructableRuleCount = cssSheet.cssRules.length;
            log('AdvancedAnalysis: constructable stylesheet rule count', constructableRuleCount);
          } catch(e){
            log('AdvancedAnalysis: replaceSync threw error (may indicate parse choke point):', e.message);
          }
        } else {
          log('AdvancedAnalysis: Constructable Stylesheets not supported, skipping parse replicate.');
        }

        // If browser-exposed rule count is low, try to locate earliest key selector relative to observed rules
        const liveMain = Array.from(document.styleSheets).find(s=>s.href===href);
        let liveRuleCount = null;
        try { liveRuleCount = liveMain && liveMain.cssRules ? liveMain.cssRules.length : null; } catch(e){ liveRuleCount = 'blocked'; }
        log('AdvancedAnalysis: live cssRules length', liveRuleCount);

        // Heuristic: binary search for maximum prefix that parses to same rule count (suggesting cutoff)
        if(typeof constructableRuleCount === 'number' && typeof liveRuleCount === 'number' && liveRuleCount < constructableRuleCount){
          log('AdvancedAnalysis: live rule count < constructable count. Live sheet likely truncated by runtime (e.g., parse abort due to injected transform).');
        } else if(typeof constructableRuleCount === 'number' && typeof liveRuleCount === 'number' && constructableRuleCount === liveRuleCount){
          log('AdvancedAnalysis: constructable parse reproduces low rule count; genuine parse truncation within CSS content. Locating approximate fault region...');
          // Binary search for first index where adding more text increases rule count
          let low = 0, high = text.length, target = liveRuleCount;
          let faultWindow = null;
          while(low + 1000 < high){
            const mid = Math.floor((low+high)/2);
            try {
              const ss = new CSSStyleSheet();
              ss.replaceSync(text.slice(0, mid));
              if(ss.cssRules.length >= target){
                high = mid;
              } else {
                low = mid;
              }
            } catch(e){
              high = mid; // parse error within prefix
            }
          }
          faultWindow = { start: Math.max(0, high-400), end: Math.min(text.length, high+400) };
          const snippet = text.slice(faultWindow.start, faultWindow.end);
          log('AdvancedAnalysis: suspected parse fault window', faultWindow);
          log('AdvancedAnalysis: snippet around fault window ->\n'+snippet);
        }

        // Report @import usage & recommendation
        const importMatches = [...text.matchAll(/@import\s+url\(/g)].map(m=>m.index);
        log('AdvancedAnalysis: @import count', importMatches.length, 'positions', importMatches.slice(0,10));
        if(importMatches.length){
          log('Recommendation: Inline Google Font links in index.html <head> as <link rel="preconnect"...> and remove @import to rule out font-loader interactions.');
        }

      } catch(e){
        log('AdvancedAnalysis error', e);
      }
    })();

    window.__STYLE_DEBUG__ = { summarizeSheets, logTokens, sampleSelectors: sampleSelectors };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[StyleDebug] Error initializing diagnostics', e);
  }
})();
